import * as React from 'react';
import type { SchoolWithDepartmentsViewModel } from '../../Blocks/SchoolsAndDepartmentsListBlock/SchoolWithDepartmentsViewModel.csharp';
import type { SchoolsAndDepartmentsListProps } from '../../Blocks/SchoolsAndDepartmentsListBlock/SchoolsAndDepartmentsListProps.csharp';
import { SchoolsAndDepartmentsListVariant } from '../../Blocks/SchoolsAndDepartmentsListBlock/SchoolsAndDepartmentsListVariant.csharp';
import { AccordionSection } from '../AccordionSection/AccordionSection';
import { Link } from '../Link/Link';
import type { LinkViewModel } from '../Link/LinkViewModel.csharp';
import { List } from '../List/List';
import { Shortcut } from '../Shortcut/Shortcut';
import type { ShortcutVariant } from '../Shortcut/ShortcutVariant';
import styles from './SchoolsAndDepartments.module.css';

export const SchoolsAndDepartments: React.FC<React.PropsWithChildren<SchoolsAndDepartmentsListProps>> = props => {
    const { title, schools, variant } = props;
    const schoolItems = schools.map((s, idx) => ({
        id: idx,
        school: s,
    }));

    const renderAccordionContent = (school: SchoolWithDepartmentsViewModel) => (
        <div className={styles.AccordionContent}>
            <p className={styles.Teaser}>{school.teaser}</p>
            <Shortcut {...school.schoolLink} variant="dark" className={styles.SchoolLink} />
            {school.departmentsLinks.length > 0 && (
                <>
                    <h4 className={styles.DepartmentsHeading}>{school.departmentsHeader}</h4>
                    {renderDepartmentsLink(school.departmentsLinks, 'dark')}
                </>
            )}
        </div>
    );

    const renderDepartmentsLink = (departmentsLinks: LinkViewModel[], shortcutVariant: ShortcutVariant) => {
        if (departmentsLinks.length > 0) {
            return (
                <List
                    items={departmentsLinks}
                    className={styles.DepartmentsList}
                    renderItem={departmentLink => (
                        <li key={departmentLink.label}>
                            <Shortcut {...departmentLink} variant={shortcutVariant} />
                        </li>
                    )}
                />
            );
        }
    };

    return (
        <section>
            {variant === SchoolsAndDepartmentsListVariant.Blue && (
                <AccordionSection
                    title={title}
                    items={schoolItems}
                    renderAccordionHeading={item => item.school.title}
                    renderAccordionBody={item => renderAccordionContent(item.school)}
                />
            )}
            {variant === SchoolsAndDepartmentsListVariant.Black && (
                <div className={styles.ListWrapper}>
                    <h2 className="visuallyHidden">{title}</h2>
                    <List
                        items={schoolItems}
                        className={styles.List}
                        renderItem={item => {
                            const { id, school } = item;

                            return (
                                <li key={id} className={styles.School}>
                                    <Link {...school.schoolLink} className={styles.SchoolLink}>
                                        <h3 className={styles.SchoolTitle}>{school.title}</h3>
                                    </Link>
                                    <div className={styles.SchoolDetails}>
                                        <div className={styles.TeaserWrapper}>
                                            {school.teaser && <p className={styles.Teaser}>{school.teaser}</p>}
                                        </div>
                                        <div className={styles.Departments}>
                                            {renderDepartmentsLink(school.departmentsLinks, 'primary')}
                                        </div>
                                    </div>
                                </li>
                            );
                        }}
                    />
                </div>
            )}
        </section>
    );
};
